"use client";

import { ArrowUpRight, CircuitBoard, Cpu, Layers, Microchip, Package, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CityDirectory() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headingWords = ["Arboriculture,", "expertly", "cultivated."];
  const capabilities = [{
    index: "01",
    title: "Precision Tree Pruning",
    description: <>From <Link to="/ResidentialTreeCarePage" className="underline underline-offset-4 hover:text-foreground">structural assessment</Link> to canopy refinement in under 48 hours. Expertly managed pruning using professional techniques and safety standards under strict guidelines.</>,
    icon: CircuitBoard,
    span: "lg:col-span-5",
    featured: true
  }, {
    index: "02",
    title: "Plant Health Care",
    description: <>Authenticated soil treatments and nutrient management with full traceability from trusted <Link to="/OurExpertisePage" className="underline underline-offset-4 hover:text-foreground">arboricultural suppliers</Link>.</>,
    icon: Package,
    span: "lg:col-span-3",
    featured: false
  }, {
    index: "03",
    title: "Risk Assessment",
    description: <>Expert <Link to="/HoaTreeCarePage" className="underline underline-offset-4 hover:text-foreground">site audits</Link> to identify hazards before they become structural issues.</>,
    icon: Layers,
    span: "lg:col-span-3",
    featured: false
  }, {
    index: "04",
    title: "Root System Care",
    description: <>Micro-scale soil aeration and <Link to="/ResidentialTreeCarePage" className="underline underline-offset-4 hover:text-foreground">root restoration</Link> for sensitive urban trees.</>,
    icon: Microchip,
    span: "lg:col-span-3",
    featured: false
  }, {
    index: "05",
    title: "Landscape Integration",
    description: <>Site planning, species selection, and initial growth validation. We bridge the gap between bare soil and a thriving ecosystem you can <Link to="/CommunitiesWeServePage" className="underline underline-offset-4 hover:text-foreground">grow upon</Link>.</>,
    icon: Cpu,
    span: "lg:col-span-4",
    featured: true
  }, {
    index: "06",
    title: "Maintenance Handoff",
    description: <>Complete care documentation packages prepared for seamless long-term <Link to="/GetQuotePage" className="underline underline-offset-4 hover:text-foreground">growth transition</Link>.</>,
    icon: Wrench,
    span: "lg:col-span-4",
    featured: false
  }];
  return <section data-section-id="5197" id="city-directory" className="bg-background text-foreground py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-16 md:mb-24 lg:mb-32">
          <div ref={headerRef} className={`lg:col-span-7 lg:col-start-1 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-8 md:mb-10" style={{
            color: "var(--chart-3)"
          }}>
              SERVICES · 03
            </p>
            <h2 className="font-default font-light text-4xl md:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-tight text-foreground">
              {headingWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden mr-3 md:mr-4">
                  <span className={`inline-block transition-all ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`} style={{
                transitionDuration: "900ms",
                transitionDelay: `${200 + i * 150}ms`
              }}>
                    {word}
                  </span>
                </span>)}
            </h2>
          </div>

          <div ref={introRef} className={`lg:col-span-3 lg:col-start-9 lg:pt-20 transition-all duration-1000 delay-300 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-base md:text-lg leading-relaxed text-foreground/70 font-default font-light mb-8">
              Six core services, refined over a decade of bringing landscape visions into healthy, sustainable reality for the most discerning property owners.
            </p>
            <div className="overflow-hidden rounded-lg border border-border shadow-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/dfw_service_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/dfw_service_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/dfw_service_map.png-wvc-sizes" alt="DFW service area map showing Fort Worth and surrounding communities" loading="lazy" className="w-full h-auto object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {capabilities.map((cap, i) => {
          const Icon = cap.icon;
          return <Card data-index={i} key={cap.index} className={`group relative ${cap.span} p-8 md:p-10 border border-border bg-card text-card-foreground rounded-lg transition-all duration-500 ease-out hover:-translate-y-1 cursor-pointer overflow-hidden ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${cap.featured ? "lg:min-h-[440px]" : "lg:min-h-[320px]"}`} style={{
            transitionDelay: gridVisible ? `${i * 100}ms` : "0ms",
            borderColor: "var(--border)"
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = "var(--chart-3)";
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "var(--border)";
          }}>
                {}
                <span className="absolute top-6 right-7 font-mono text-xs tracking-widest" style={{
              color: "var(--chart-3)"
            }}>
                  {cap.index}
                </span>

                <div className="flex flex-col h-full">
                  {}
                  <div className={`flex items-center justify-center rounded-lg mb-8 transition-colors duration-500 ${cap.featured ? "h-32 md:h-40" : "h-20 md:h-24"}`} style={{
                backgroundColor: "var(--muted)"
              }}>
                    <Icon className={`${cap.featured ? "size-16 md:size-20" : "size-10 md:size-12"} transition-transform duration-500 group-hover:scale-110`} style={{
                  color: "var(--chart-3)"
                }} strokeWidth={1.25} />
                  </div>

                  {}
                  <h3 className={`font-default font-light leading-tight mb-4 transition-colors duration-300 text-foreground group-hover:text-[var(--chart-3)] ${cap.featured ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`}>
                    {cap.title}
                  </h3>

                  {}
                  <p className="text-sm leading-relaxed text-foreground/65 font-default mb-6 flex-grow">
                    {cap.description}
                  </p>

                  {}
                  <Link to="/OurExpertisePage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-foreground transition-colors duration-300 group-hover:text-[var(--chart-3)]">
                    <span>Explore</span>
                    <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </Card>;
        })}
        </div>

        {}
        <div className="mt-20 md:mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-12 border-t border-border">
          <p className="font-default font-light text-xl md:text-2xl text-foreground/80 max-w-xl leading-snug">
            A professional consultation begins with a <Link to="/GetQuotePage" className="underline underline-offset-4 hover:text-foreground">single conversation</Link>.
          </p>
          <Button asChild size="lg" className="rounded-lg px-8 h-12 font-mono text-xs tracking-[0.15em] uppercase" style={{
          backgroundColor: "var(--chart-3)",
          color: "var(--primary-foreground)"
        }}>
            <Link to="/GetQuotePage">
              Request a Quote
              <ArrowUpRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}